import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ACCOUNT_KINDS } from '@subs/shared';

import {
  config,
  moneyAccounts,
  vendorGroups,
  type MoneyAccount,
  type VendorGroup,
} from '../api/client';
import { useToast } from '../components/Toast';
import { ApiStatusChips } from '../components/ApiStatusChips';
import { AuditPanel } from '../components/AuditPanel';
import { BackupPanel } from '../components/BackupPanel';
import { UsersPanel } from '../components/UsersPanel';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  Textarea,
} from '../components/ui';
import { formatBytes } from '../lib/format';
import { useSession } from '../lib/use-session';

type SettingsTab = 'general' | 'users' | 'audit' | 'backup';

const TABS: { id: SettingsTab; label: string; adminOnly: boolean }[] = [
  { id: 'general', label: 'General', adminOnly: false },
  { id: 'users', label: 'Users', adminOnly: true },
  { id: 'audit', label: 'Audit', adminOnly: true },
  { id: 'backup', label: 'Backup', adminOnly: true },
];

/**
 * Money accounts + vendor groups CRUD, the runtime status, and — for an ADMIN —
 * the Users and Audit tabs (TASKS.md B4/B6). The admin-only tabs are not merely
 * disabled for a viewer: they are not offered at all, which is the same rule the
 * api applies (it would answer 403).
 */
export function SettingsPage() {
  const { isAdmin } = useSession();
  const [tab, setTab] = useState<SettingsTab>('general');
  const visible = TABS.filter((entry) => !entry.adminOnly || isAdmin);
  const active = visible.some((entry) => entry.id === tab) ? tab : visible[0]?.id ?? 'general';

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Settings</h1>
          <p className="page-header__sub">
            Money accounts, vendor groups
            {isAdmin ? ', user access, the audit trail and backups' : ''} and the runtime
            status.
          </p>
        </div>
      </div>

      <div
        role="tablist"
        aria-label="Settings sections"
        style={{ display: 'flex', gap: 'var(--space-1)', borderBottom: '1px solid var(--border)', marginBottom: 'var(--space-4)' }}
      >
        {visible.map((entry) => {
          const selected = entry.id === active;
          return (
            <button
              key={entry.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setTab(entry.id)}
              className={selected ? 'link-btn' : undefined}
              style={{
                appearance: 'none',
                background: 'transparent',
                border: 'none',
                borderBottom: `2px solid ${selected ? 'var(--accent, var(--text-primary, currentColor))' : 'transparent'}`,
                color: selected ? 'var(--text-primary)' : 'var(--text-secondary)',
                cursor: 'pointer',
                font: 'inherit',
                fontWeight: 600,
                padding: 'var(--space-2) var(--space-3)',
              }}
            >
              {entry.label}
            </button>
          );
        })}
      </div>

      {active === 'general' ? (
        <div className="settings-grid">
          <MoneyAccountsCard />
          <VendorGroupsCard />
          <StatusCard />
        </div>
      ) : null}
      {active === 'users' ? <UsersPanel /> : null}
      {active === 'audit' ? <AuditPanel /> : null}
      {active === 'backup' ? <BackupPanel /> : null}
    </>
  );
}

// ---------------------------------------------------------------------------.

function MoneyAccountsCard() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { canEdit } = useSession();
  const [editing, setEditing] = useState<MoneyAccount | null>(null);
  const [open, setOpen] = useState(false);

  const list = useQuery({
    queryKey: ['money-accounts', 200],
    queryFn: ({ signal }) => moneyAccounts.list({ limit: 200 }, signal),
  });

  const remove = useMutation({
    mutationFn: (id: string) => moneyAccounts.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['money-accounts'] });
      toast('Account deleted', 'success');
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  const items = list.data?.items ?? [];

  return (
    <div className="card">
      <div className="page-header" style={{ marginBottom: 'var(--space-3)' }}>
        <h2 className="card__title" style={{ margin: 0 }}>Money accounts</h2>
        {canEdit ? (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            New account
          </Button>
        ) : null}
      </div>

      {list.isPending ? (
        <Spinner label="Loading accounts" />
      ) : list.isError ? (
        <ErrorState message={list.error.message} onRetry={() => void list.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title="No accounts" hint="Add the bank/card accounts the subscriptions are paid from." />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Kind</th>
                <th scope="col" className="num">Currency</th>
                <th scope="col" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {items.map((account) => (
                <tr key={account.id}>
                  <td className="cell-primary">
                    <span className="cell-clip" title={account.name}>
                      {account.name}
                    </span>
                  </td>
                  <td>
                    <Badge tone="brand">{account.kind}</Badge>
                  </td>
                  <td className="num">{account.currency}</td>
                  <td>
                    <div className="row-actions">
                      {canEdit ? (
                        <>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              setEditing(account);
                              setOpen(true);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            loading={remove.isPending && remove.variables === account.id}
                            onClick={() => remove.mutate(account.id)}
                          >
                            Delete
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <MoneyAccountModal
        open={open}
        account={editing}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
      />
    </div>
  );
}

function MoneyAccountModal({
  open,
  account,
  onClose,
}: {
  open: boolean;
  account: MoneyAccount | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [kind, setKind] = useState<'BANK' | 'CARD' | 'SERVICE'>('BANK');
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('RUB');
  const [meta, setMeta] = useState('');

  const seedKey = `${account?.id ?? 'new'}-${open}`;
  useEffect(() => {
    if (!open) return;
    setKind(account?.kind ?? 'BANK');
    setName(account?.name ?? '');
    setCurrency(account?.currency ?? 'RUB');
    setMeta(account?.meta ? JSON.stringify(account.meta, null, 2) : '');
     
  }, [seedKey]);

  const mutation = useMutation({
    mutationFn: () => {
      const parsedMeta = meta.trim() ? (JSON.parse(meta) as Record<string, unknown>) : null;
      const payload = { kind, name: name.trim(), currency: currency.trim().toUpperCase(), meta: parsedMeta };
      return account ? moneyAccounts.update(account.id, payload) : moneyAccounts.create(payload);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['money-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      toast(account ? `Updated ${saved.name}` : `Created ${saved.name}`, 'success');
      onClose();
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  const submit = () => {
    if (!name.trim() || currency.trim().length !== 3) return;
    if (meta.trim()) {
      try {
        JSON.parse(meta);
      } catch {
        toast('Extra metadata must be valid JSON', 'error');
        return;
      }
    }
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={account ? 'Edit money account' : 'New money account'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={mutation.isPending} onClick={submit}>
            {account ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <Field label="Kind" htmlFor="acc-kind">
        <Select id="acc-kind" value={kind} onChange={(event) => setKind(event.target.value as 'BANK' | 'CARD' | 'SERVICE')}>
          {ACCOUNT_KINDS.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Name" htmlFor="acc-name" required>
        <Input id="acc-name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Freedom USD …848" />
      </Field>
      <Field label="Currency" htmlFor="acc-currency" required hint="3-letter code">
        <Input
          id="acc-currency"
          maxLength={3}
          value={currency}
          onChange={(event) => setCurrency(event.target.value.toUpperCase())}
        />
      </Field>
      <Field label="Metadata (JSON)" htmlFor="acc-meta" hint="e.g. a masked card number">
        <Textarea id="acc-meta" value={meta} onChange={(event) => setMeta(event.target.value)} placeholder='{"last4":"0848"}' />
      </Field>
    </Modal>
  );
}

// ---------------------------------------------------------------------------.

function VendorGroupsCard() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { canEdit } = useSession();
  const [editing, setEditing] = useState<VendorGroup | null>(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [note, setNote] = useState('');

  const list = useQuery({
    queryKey: ['vendor-groups', 200],
    queryFn: ({ signal }) => vendorGroups.list({ limit: 200 }, signal),
  });

  const remove = useMutation({
    mutationFn: (id: string) => vendorGroups.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-groups'] });
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      toast('Group deleted — subscriptions keep their history', 'success');
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  const mutation = useMutation({
    mutationFn: () => {
      const payload = { name: name.trim(), note: note.trim() || null };
      return editing ? vendorGroups.update(editing.id, payload) : vendorGroups.create(payload);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['vendor-groups'] });
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      toast(editing ? `Updated ${saved.name}` : `Created ${saved.name}`, 'success');
      setOpen(false);
      setEditing(null);
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  const items = list.data?.items ?? [];

  return (
    <div className="card">
      <div className="page-header" style={{ marginBottom: 'var(--space-3)' }}>
        <h2 className="card__title" style={{ margin: 0 }}>Vendor groups</h2>
        {canEdit ? (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setEditing(null);
              setName('');
              setNote('');
              setOpen(true);
            }}
          >
            New group
          </Button>
        ) : null}
      </div>

      {list.isPending ? (
        <Spinner label="Loading groups" />
      ) : list.isError ? (
        <ErrorState message={list.error.message} onRetry={() => void list.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title="No groups" hint="Group the bought-out instances of one vendor (e.g. Z.ai)." />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Note</th>
                <th scope="col" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {items.map((group) => (
                <tr key={group.id}>
                  <td className="cell-primary">
                    <span className="cell-clip" title={group.name}>
                      {group.name}
                    </span>
                  </td>
                  <td className="cell-muted">
                    <span className="cell-clip" title={group.note ?? undefined}>{group.note ?? '—'}</span>
                  </td>
                  <td>
                    <div className="row-actions">
                      {canEdit ? (
                        <>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              setEditing(group);
                              setName(group.name);
                              setNote(group.note ?? '');
                              setOpen(true);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            loading={remove.isPending && remove.variables === group.id}
                            onClick={() => remove.mutate(group.id)}
                          >
                            Delete
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        title={editing ? 'Edit vendor group' : 'New vendor group'}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setOpen(false);
                setEditing(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={mutation.isPending}
              disabled={!name.trim()}
              onClick={() => mutation.mutate()}
            >
              {editing ? 'Save' : 'Create'}
            </Button>
          </>
        }
      >
        <Field label="Name" htmlFor="group-name" required>
          <Input id="group-name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Z.ai" />
        </Field>
        <Field label="Note" htmlFor="group-note">
          <Textarea id="group-note" value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------.

function StatusCard() {
  const publicConfig = useQuery({
    queryKey: ['config', 'public'],
    queryFn: ({ signal }) => config.public(signal),
    staleTime: 60_000,
  });

  const data = publicConfig.data;

  return (
    <div className="card">
      <h2 className="card__title">Runtime status</h2>
      {publicConfig.isPending ? (
        <Spinner label="Reading configuration" />
      ) : publicConfig.isError ? (
        <ErrorState message={publicConfig.error.message} onRetry={() => void publicConfig.refetch()} />
      ) : data ? (
        <>
          <dl className="env-list">
            <div className="env-list__row">
              <dt className="env-list__key">Environment</dt>
              <dd className="env-list__value">
                <Badge tone={data.env === 'production' ? 'warn' : 'ok'}>{data.env}</Badge>
              </dd>
            </div>
            <div className="env-list__row">
              <dt className="env-list__key">Auth</dt>
              <dd className="env-list__value">{data.authDisabled ? 'disabled (dev)' : 'Yandex ID OAuth'}</dd>
            </div>
            <div className="env-list__row">
              <dt className="env-list__key">S3 bucket</dt>
              <dd className="env-list__value">
                {data.s3.configured ? (
                  <>
                    {data.s3.bucket ?? '—'} <Badge tone="ok">configured</Badge>
                  </>
                ) : (
                  <Badge tone="bad">not configured</Badge>
                )}
              </dd>
            </div>
            <div className="env-list__row">
              <dt className="env-list__key">S3 region</dt>
              <dd className="env-list__value">{data.s3.region}</dd>
            </div>
            <div className="env-list__row">
              <dt className="env-list__key">LLM model</dt>
              <dd className="env-list__value">
                {data.llm.configured ? (
                  <>
                    {data.llm.model} <Badge tone="ok">ready</Badge>
                  </>
                ) : (
                  <Badge tone="bad">no key</Badge>
                )}
              </dd>
            </div>
            <div className="env-list__row">
              <dt className="env-list__key">Bill upload limit</dt>
              <dd className="env-list__value">{formatBytes(data.bills.maxUploadBytes)}</dd>
            </div>
            <div className="env-list__row">
              <dt className="env-list__key">Accepted bill types</dt>
              <dd className="env-list__value">{data.bills.allowedMimes.length} media types</dd>
            </div>
          </dl>
          <h3 className="section-title">Dependencies</h3>
          <ApiStatusChips />
        </>
      ) : null}
    </div>
  );
}
